import { TcoService } from '@amzn/awscat-portfolio-assessment-service-client';
import { OutputLaborSupport } from '@amzn/awscat-portfolio-assessment-types';
import { FlashContextInterface, FlashType, withFlashContext } from '@amzn/awscat-react-components';
import { Container, Header, SpaceBetween, Spinner, Table, Tabs, TabsProps } from '@amzn/awsui-components-react';
import { useContext, useEffect, useState } from 'react';
import { match, useRouteMatch } from 'react-router-dom';
import { useIsMounted } from 'react-tidy';

import { HelpPanelTopicOptions } from '../../../common/Constants';
import { constants } from '../../../common/Localization';
import { MatchParam } from '../../../common/types';
import { HelpPanelContext } from '../../../contexts/HelpPanelContext';
import { TcoContext } from '../../../contexts/TcoContext';
import { buildITLaborAndSupportSummary } from '../common/Common';
import { commonTotalCostSummary, generateCashFlowTable, summaryColumnDefs } from '../common/Common';
import { CashFlowData, CommonSummaryItem } from '../common/types';

type Props = FlashContextInterface;

const tcoService = new TcoService();

function ITLaborSupportCost({ flash }: Props) {
    const match: match<MatchParam> | null = useRouteMatch({
        path: '/portfolio/:portfolioId/tco/:tcoAssessmentId',
    });
    const tcoAssessmentId = match?.params.tcoAssessmentId ?? null;
    const { assessment, tcoLoading } = useContext(TcoContext);
    const { panelHandler } = useContext(HelpPanelContext);

    const [ loading, setLoading ] = useState(false);
    const [ activeTabId, setActiveTabId ] = useState('itLabor');
    const [ laborSupportOutput, setLaborSupportOutput ] = useState<OutputLaborSupport | null>(null);

    const isMounted = useIsMounted();

    const loadLaborSupportData = async () => {
        if (!isMounted()) {
            return;
        }
        
        setLoading(true);
        try {
            if (tcoAssessmentId && tcoAssessmentId !== 'default' && assessment) {
                const laborSupportAssessment = await tcoService.getAwsSupportAndLabor(tcoAssessmentId);
                if (isMounted()) {
                    setLaborSupportOutput(laborSupportAssessment.output.aws.supportAndLabor);
                }
            }
        } catch(err) {
            if (isMounted()) {
                flash.alert(FlashType.error, (err as Error).message, constants.ERROR_LOADING_RDS_ASSESSMENT);
            }
        } finally {
            if (isMounted()) {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        panelHandler(HelpPanelTopicOptions.AWS_IT_AND_SUPPORT);
    }, [ panelHandler ]);

    useEffect(() => {
        if (assessment) {
            loadLaborSupportData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ assessment ]);

    const buildSummaryItems = (output: OutputLaborSupport): CommonSummaryItem[] => [
        {
            type: constants.IT_LABOR,
            cost: output.labor.summary.totalCost,
        },
        {
            type: constants.AWS_SUPPORT_COST,
            cost: output.support.summary.totalAWSSupportCost,
        }
    ];

    const buildLaborCashFlowItems = (cost: OutputLaborSupport): CashFlowData[] => {
        const cashFlowData: CashFlowData[] = [];
        const ec2AdminCost = {
            type: constants.AWS_EC2_ADMIN_COST,
        };
        const awsDHAdminCost = {
            type: constants.AWS_DH_ADMIN_COST,
        };
        const awsDBAdminCost = {
            type: constants.AWS_DB_ADMIN_COST,
        };
        const awsStorageAdminCost = {
            type: constants.AWS_STORAGE_ADMIN_COST,
        };

        cost.costsByYear.forEach((yearlyCost, year) => {
            (ec2AdminCost as CashFlowData)['year' + year] = yearlyCost.ec2AdminCost;
            (awsDHAdminCost as CashFlowData)['year' + year] = yearlyCost.dedicatedHostAdminCost;
            (awsDBAdminCost as CashFlowData)['year' + year] = yearlyCost.databaseAdminCost;
            (awsStorageAdminCost as CashFlowData)['year' + year] = yearlyCost.storageAdminCost;
        });
        cashFlowData.push(ec2AdminCost as CashFlowData);
        if (cost.summary.ec2.numberDedicatedHostInstances) {
            cashFlowData.push(awsDHAdminCost as CashFlowData);
        }
        cashFlowData.push(awsDBAdminCost as CashFlowData, awsStorageAdminCost as CashFlowData);
        
        return cashFlowData;
    };

    const buildSupportCashFlowItems = (cost: OutputLaborSupport): CashFlowData[] => {
        const cashFlowData: CashFlowData[] = [];
        const supportPlanCost = {
            type: constants.SUPPORT_PLAN_COST,
        };
        const partnerPrice = {
            type: constants.PARTNER_PRICE,
        };

        cost.costsByYear.forEach((yearlyCost, year) => {
            (supportPlanCost as CashFlowData)['year' + year] = yearlyCost.supportPlanCost;
            (partnerPrice as CashFlowData)['year' + year] = yearlyCost.partnerPrice;
        });
        cashFlowData.push(supportPlanCost as CashFlowData, partnerPrice as CashFlowData);
        
        return cashFlowData;
    };

    const buildTabs = (laborAndSupportCost: OutputLaborSupport, currency: string, duration: number): TabsProps.Tab[] => [
        {
            id: 'itLabor',
            label: constants.IT_LABOR,
            content: generateCashFlowTable(buildLaborCashFlowItems(laborAndSupportCost), duration, currency),
        },
        {
            id: 'awsSupport',
            label: constants.AWS_SUPPORT,
            content: generateCashFlowTable(buildSupportCashFlowItems(laborAndSupportCost), duration, currency),
        }
    ];

    const buildLaborSupportPageContent = (): JSX.Element => {
        if (loading || !laborSupportOutput || !assessment) {
            return (
                <Spinner size='big' />
            );
        }

        const currency = laborSupportOutput.currency;
        return (
            <SpaceBetween size='xl'>
                <Header variant='h1'>{constants.IT_LABOR_SUPPORT_TITLE}</Header>
                <Table
                    columnDefinitions={summaryColumnDefs(currency)}
                    items={buildSummaryItems(laborSupportOutput)}
                />
                {/* TODO: after adjustments release, commonTotalCostSummary should be removed 
                https://i.amazon.com/issues/PAT-32572 */}
                { laborSupportOutput.summary.supportManualAdjustment ?
                    buildITLaborAndSupportSummary(laborSupportOutput.summary.totalCost, laborSupportOutput.summary.manualAdjustment,
                        laborSupportOutput.summary.supportManualAdjustment, laborSupportOutput.summary.adjustedTotalCost, currency) : 
                    commonTotalCostSummary(
                        currency, laborSupportOutput.summary.totalCost,
                        laborSupportOutput.summary.manualAdjustment,
                        laborSupportOutput.summary.adjustedTotalCost,
                    )
                }
                <Header variant='h2'>{constants.DETAILS}</Header>
                <Tabs
                    tabs={buildTabs(laborSupportOutput, currency, assessment.configuration.tcoAssumptions.durationYears)}
                    activeTabId={activeTabId}
                    onChange={({ detail }) => setActiveTabId(detail.activeTabId)}
                />
            </SpaceBetween>
        );
    };

    return !tcoLoading ? (
        <Container data-testid='container'>
            {buildLaborSupportPageContent()}
        </Container>
    )
        : null;

}

export default withFlashContext(ITLaborSupportCost);
